import { useEffect, useState } from 'react'
import { GET_POSTS } from '../assets/api/apiCalls'
import { Post } from '../shared/types'
import { useTranslation } from 'next-i18next'
import { useQuery } from '@apollo/client'
import { categoryTitle } from './misc/titles'
import dynamic from 'next/dynamic'
import { Icon } from '@iconify/react'

const ArticleCard = dynamic(() => import('./ArticleCard').then(mod => mod.ArticleCard), { ssr: false })

export function CityContentList({cityName, locale}: {cityName: string, locale: string}) {
    const [postPagination, setPostPagination] = useState<number>(0)
    const { t } = useTranslation()

    const { loading: postsLoading, error: postsError, data: postsData } = useQuery(GET_POSTS, {
        nextFetchPolicy: 'cache-first',
        variables: {
            "locale": locale,
            "city": cityName,
            "pagination": postPagination
        },
        skip: cityName === undefined || locale === undefined
    })

    useEffect(() => {
        setPostPagination(0)
    }, [locale])

    const handleNextPage = (type: string) => {
        setPostPagination(postPagination + 1)
    }
    
    const handlePreviousPage = (type: string) => {
        setPostPagination(postPagination - 1)
    }

    const loadingAnimation = () => {
        return (
            <div className="bg-white p-4 rounded animate-pulse">
                <div className="h-4 w-full h-[3rem] mb-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                <div className="h-4 w-3/4 bg-gray-300 dark:text-gray-300 rounded"></div>
                <ul role="list" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 justify-center gap-5 mb-5 mt-5">
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                </ul>
            </div>
        )
    }

    return (
        <>  
            {postsLoading ? (
                loadingAnimation()
            ) : (
                <>
                    {categoryTitle({title: t("city.all_posts")})}
                    {postsData?.posts?.data?.length > 0 ? (
                        <>
                            <ul role="list" className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 justify-center gap-5 mb-5 transition-opacity duration-500 ${postsData?.posts?.data?.length ? 'opacity-100' : 'opacity-0'}`}>
                                {postsData?.posts?.data?.map((post: Post) => (
                                    <ArticleCard post={post} key={post?.id} mainPost={false} />
                                ))}
                            </ul>
                            {postsData?.posts?.meta !== null && postsData?.posts?.meta?.pagination?.pageCount > 1 && (
                                <div className="flex flex-row gap-2 justify-center mx-auto">
                                    <p className={`${postPagination+1 === 1 ? "collapse" : "visible"}`} onClick={() => handlePreviousPage("post")}>{"<"}</p>
                                    <p>{t("search.pagination", { page: postPagination+1, pages: postsData?.posts?.meta?.pagination?.pageCount?.toString()})}</p>
                                    <p className={`${postPagination+1 === postsData?.posts?.meta?.pagination?.pageCount ? "collapse" : "visible"}`} onClick={() => handleNextPage("post")}>{">"}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex flex-row gap-1 items-starttext-md bg-gray-200 p-1 rounded-xl">
                            <span className="text-2xl">
                                <Icon icon="mdi:face-sad-outline" />
                            </span>
                            <p>{t("common.no_posts")}</p>
                        </div>
                    )}
                </>
            )}
        </>
    )
}